import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import {
  OrganizationService,
  AcceptInviteRequest,
  orgInviteEmployeeStorageKey,
} from "src/app/Services/organization.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { TokenService } from "src/app/Services/token.service";
import { inviteScopeLabel } from "src/app/shared/org-access-roles";

/**
 * Invite-only join page. Users cannot browse organizations or request to join;
 * membership starts from an organization invite only.
 */
@Component({
  selector: "app-org-join",
  templateUrl: "./org-join.component.html",
  styleUrls: ["./org-join.component.scss"],
})
export class OrgJoinComponent implements OnInit {
  loading = true;
  busy = false;
  myInvites: any[] = [];
  inviteIdFromQuery = "";
  organizationIdFromQuery = "";
  readonly inviteScopeLabel = inviteScopeLabel;

  constructor(
    private organizationService: OrganizationService,
    private orgContext: OrgContextService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private authModal: AuthModalService,
    private tokenService: TokenService
  ) {}

  ngOnInit(): void {
    this.inviteIdFromQuery =
      this.route.snapshot.queryParamMap.get("inviteId") || "";
    this.organizationIdFromQuery =
      this.route.snapshot.queryParamMap.get("organizationId") || "";

    if (!this.isAuthenticated()) {
      const returnUrl = this.buildJoinReturnUrl();
      try {
        localStorage.setItem("returnUrl", returnUrl);
      } catch {
        /* ignore */
      }
      this.authModal.openModal("login", returnUrl);
      this.loading = false;
      return;
    }

    this.loadInvites();
  }

  private isAuthenticated(): boolean {
    if (!this.tokenService.getRefreshToken()) {
      return false;
    }
    const raw =
      localStorage.getItem("LoggedInUser") || localStorage.getItem("user");
    if (!raw || raw === "null") {
      return false;
    }
    try {
      const parsed = JSON.parse(raw);
      return !!(parsed?.emailAddress || parsed?.EmailAddress || parsed?.userName);
    } catch {
      return !!raw;
    }
  }

  private buildJoinReturnUrl(): string {
    const params = new URLSearchParams();
    if (this.inviteIdFromQuery) {
      params.set("inviteId", this.inviteIdFromQuery);
    }
    if (this.organizationIdFromQuery) {
      params.set("organizationId", this.organizationIdFromQuery);
    }
    const qs = params.toString();
    return qs ? `/org/join?${qs}` : "/org/join";
  }

  private loadInvites(): void {
    this.loading = true;
    this.organizationService.myInvites().subscribe({
      next: (invites) => {
        this.myInvites = invites || [];
        this.loading = false;
        if (this.inviteIdFromQuery) {
          const match = this.myInvites.find(
            (i) => (i.inviteId || i.InviteId) === this.inviteIdFromQuery
          );
          if (match) {
            this.acceptInvite(match);
          } else {
            this.toastr.info(
              "Invite not found for your account. Sign in with the invited email, then accept."
            );
          }
        }
      },
      error: () => {
        this.myInvites = [];
        this.loading = false;
      },
    });
  }

  private readStashedEmployeeDetails(inviteId: string): AcceptInviteRequest | null {
    if (!inviteId) {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(orgInviteEmployeeStorageKey(inviteId));
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as AcceptInviteRequest;
    } catch {
      return null;
    }
  }

  private clearStashedEmployeeDetails(inviteId: string): void {
    if (!inviteId) {
      return;
    }
    try {
      sessionStorage.removeItem(orgInviteEmployeeStorageKey(inviteId));
    } catch {
      /* ignore */
    }
  }

  acceptInvite(invite: any): void {
    const orgId = invite.organizationId || invite.OrganizationId;
    const inviteId = invite.inviteId || invite.InviteId;
    const appId = invite.applicationId || invite.ApplicationId || null;
    const teamId = invite.teamId || invite.TeamId || null;
    if (!orgId || !inviteId) return;
    this.busy = true;
    const details = this.readStashedEmployeeDetails(inviteId);
    this.organizationService.acceptInvite(orgId, inviteId, details).subscribe({
      next: (membership) => {
        this.clearStashedEmployeeDetails(inviteId);
        const preferredApp =
          membership?.applicationId ||
          membership?.ApplicationId ||
          appId ||
          null;
        const preferredTeam =
          membership?.teamId || membership?.TeamId || teamId || null;

        this.orgContext
          .enterOrganizationMode(orgId)
          .subscribe({
            next: () => {
              if (preferredApp) {
                this.orgContext.setActiveApplication(preferredApp);
              }
              if (preferredTeam) {
                this.orgContext.setActiveTeam(preferredTeam);
              }
              this.busy = false;
              this.toastr.success(
                "Invite accepted — your organization settings are updated"
              );
              this.myInvites = this.myInvites.filter(
                (i) => (i.inviteId || i.InviteId) !== inviteId
              );
              this.router.navigate(["/"]);
            },
            error: () => {
              this.busy = false;
              this.toastr.success("Invite accepted");
              this.router.navigate(["/"]);
            },
          });
      },
      error: (err) => {
        this.busy = false;
        this.toastr.error(err?.error?.error || "Could not accept invite");
      },
    });
  }
}
